import React, { useState, useEffect, memo, useRef } from "react";

import apiRequest from "../../../utils/ApiRequest/apiRequest.js";
import Button from "../../Elements/Button/Button.js";
import config from "config";
import Form from "../../Reusable/Form/Form.js";
import Login from "components/Structure/Login/Login.js";
import Icon from "../../Elements/Icon/Icon.tsx";
import useUpdatePasswordRequirements from "hooks/useUpdatePasswordRequirements.ts";
import LoadingImageElement from "components/Elements/LoadingContainerElement/LoadingImageElement.tsx";
import Recaptcha from "components/Reusable/Recaptcha/Recaptcha.tsx";
import { RECAPTCHA_APPEAR_DELAY } from "utils/globalConsts.ts";
import { useContent } from "../../../contexts/contentContext/contentContext.js";
import { useToasts } from "react-toast-notifications";
import { useParams } from "react-router-dom";
import { ResetPasswordFields } from "./ResetPasswordFields.js";
import { useCookie } from "contexts/cookieContext/cookieContext.js";
import { useModal } from "contexts/modalContext/modalContext.js";
import { useUser } from "contexts/userContext/userContext.js";
import { awaiter } from "../Home/Helpers/helpers.js";

import "../JumboCodesRecovery/JumboCodesRecovery.scss";
import "./ResetPassword.scss";

const Success = memo(() => {
  const { getTextByKey } = useContent();
  const { setModal } = useModal();

  const handleLogin = () => {
    setModal({
      modal: Login,
      modalSize: "full-modal",
    });
  };

  return (
    <>
      <p>{getTextByKey("reset-password-success-text")}</p>
      <Button onClick={handleLogin}>{getTextByKey("login")}</Button>
    </>
  );
});

const ResetPassword = () => {
  const { getTextByKey, getLanguageCulture, getPath, getBasicSettingsData } =
    useContent();
  const { setUserDataProp } = useUser();
  const params = useParams();
  const [expired, setExpired] = useState(false);
  const [validToken, setValidToken] = useState(true);
  const [emailToSend, setEmailToSend] = useState("");
  const [loading, setLoading] = useState(false);
  const { setCookies } = useCookie();
  const { addToast } = useToasts();
  const [success, setSuccess] = useState(false);
  const [showRecaptcha, setShowRecaptcha] = useState(false);

  const recaptchaRef = useRef(null);

  const [captchaToken, setCaptchaToken] = useState(null);

  useEffect(() => {
    if (params.token !== undefined) {
      DescryptTokenResetPassword();
    }
  }, [params.token]);

  const showError = () => {
    addToast(getTextByKey("reset-password-error-alert"), {
      appearance: "error",
    });
    setSuccess(false);
  };

  const RecoverPasswordUserRequestReact = async () => {
    setLoading(true);
    setShowRecaptcha(true);

    if (getBasicSettingsData().recaptchaType === "google" && !captchaToken) {
      await awaiter(RECAPTCHA_APPEAR_DELAY);
      await recaptchaRef.current.executeAsync();

      return;
    }

    if (!captchaToken) {
      addToast(getTextByKey("recaptcha-verification-error"), {
        appearance: "error",
      });
      setLoading(false);
      return;
    }

    const parameters = {
      userEmail: emailToSend,
      didRecaptcha: true,
      culture: getLanguageCulture(),
      recaptchaResponse: captchaToken,
    };
    const response = await apiRequest(
      "Users",
      "RecoverPasswordUserRequestReact",
      parameters
    );

    if (response.status === "success") {
      if (response.data) {
        addToast(getTextByKey("expire-time-success-alert-resetpassword"), {
          appearance: "success",
        });
      } else {
        showError();
      }
    } else {
      showError();
    }

    setLoading(false);
  };

  const DescryptTokenResetPassword = async () => {
    setLoading(true);
    const parameters = {
      token: params.token,
    };
    const response = await apiRequest(
      "Users",
      "DescryptTokenResetPassword",
      parameters
    );

    if (response.status === "success") {
      if (response.data) {
        setUserDataProp({
          key: "EnforceStrongPass",
          value: response.data?.EnforceStrongPass,
        });
        setValidToken(true);
        if (response.data.status === "expired") {
          setExpired(true);
          setEmailToSend(response.data.UserEmail);
        } else if (response.data.status === "no-token") {
          setValidToken(false);
          showError();
        } else {
          setExpired(false);
          setEmailToSend(response.data.UserEmail);
        }
      } else {
        showError();
      }
    } else {
      setValidToken(false);
      showError();
    }
    setLoading(false);
  };

  const formFields = useUpdatePasswordRequirements({
    defaultFields: ResetPasswordFields,
    fieldName: "Password",
    confirmField: "ConfirmPassword",
  });

  const updatePassword = async (body) => {
    const bodyObject = {
      ...body,
      token: params.token,
      RecaptchaResponse: body.recaptchaResponse,
      V3Recatcha: false,
      Culture: getLanguageCulture(),
      // captchaServiceName: "cloudflare",
    };

    if (body.Password.length < 5) {
      return {
        status: "error",
        message: "reset-password-short-alert",
      };
    } else if (body.ConfirmPassword.length > 50) {
      return {
        status: "error",
        message: "reset-password-long-alert",
      };
    } else if (body.Password !== body.ConfirmPassword) {
      return { status: "error", message: "reset-password-invalid-confirm" };
    } else {
      setLoading(true);
      const response = await apiRequest(
        "Users",
        "ResetPasswordFromUserReact",
        bodyObject,
        "POST"
      );
      if (response.status === "success" && response.data === true) {
        setLoading(false);
        setSuccess(true);

        setCookies("loginDate", new Date().getTime(), {
          path: "/",
          domain: ".jumbomail.me",
        });
      } else {
        const errMessage = response.data || "reset-password-error-alert";
        setLoading(false);
        addToast(getTextByKey(errMessage), {
          appearance: "error",
        });
        return { status: "error", message: errMessage };
      }
      return response;
    }
  };

  useEffect(() => {
    if (captchaToken) {
      RecoverPasswordUserRequestReact();

      setCaptchaToken(null);
      setShowRecaptcha(false);
    }
  }, [captchaToken]);

  useEffect(() => {
    if (!validToken) {
      window.location.href = config.baseurl + getPath() + "error";
    }
  }, [validToken]);

  return (
    <div className="full-page">
      <div className="page-container">
        <div className="title-page">
          <h1>{getTextByKey("reset-password-title")}</h1>
          <div className="title-divider"></div>
        </div>
        {!loading ? (
          !success ? (
            <div className="main-page reset-password-main">
              {!expired && validToken ? (
                <>
                  <p>{getTextByKey("reset-password-text")}</p>
                  <Form
                    fieldsProps={{ field: "square" }}
                    id="reset-password-form"
                    fields={formFields}
                    onSubmit={updatePassword}
                    submitProps={{
                      title: getTextByKey("save"),
                    }}
                    buttonProps={{
                      buttonsSize: "xs",
                      buttonsDirection: "pull-to-start",
                    }}
                    // captchaProvider="cloudflare"
                    // cloudflareActionName="resetPassword"
                  />
                </>
              ) : (
                <div className="expired-reset-passowrd">
                  <Icon iconClass="no-forms-img" name="no-forms" xxxl="true" />
                  <h2>{getTextByKey("expire-time-title-resetpassword")}</h2>
                  <p>{getTextByKey("expire-time-text-resetpassword")}</p>
                  <p>
                    {getTextByKey("expire-time-email-send-to-resetpassword")}{" "}
                    {emailToSend}
                  </p>
                  <Button onClick={() => RecoverPasswordUserRequestReact()}>
                    {getTextByKey("resend-button-resetpassword")}
                  </Button>
                </div>
              )}
              {showRecaptcha && (
                <Recaptcha
                  recaptchaRef={recaptchaRef}
                  onSuccess={(token) => setCaptchaToken(token)}
                />
              )}
            </div>
          ) : (
            <div className="main-page reset-password-main">
              <Success />
            </div>
          )
        ) : (
          <div className="loading-blog-container">
            <LoadingImageElement />
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
